import React, { useState } from 'react';

/**
 * PasswordProtection Component
 * 
 * A reusable component that wraps children and shows a password prompt
 * if the user is not authenticated. Authentication state is persisted
 * in sessionStorage for the browser session.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render when authenticated
 * @param {string} props.password - The correct password required for access
 * @param {string} props.storageKey - Key for sessionStorage (default: 'password_protected')
 * @param {string} props.title - Title shown in password prompt (default: 'Password Required')
 * @param {string} props.description - Description shown in password prompt (default: 'Please enter the password to access this page.')
 * @param {string} props.placeholder - Placeholder text for password input (default: 'Enter password')
 * @param {string} props.submitButtonText - Text for submit button (default: 'Submit')
 * @param {string} props.errorMessage - Error message shown on incorrect password (default: 'Incorrect password. Please try again.')
 */
const PasswordProtection = ({
  children,
  password,
  storageKey = 'password_protected',
  title = 'Password Required',
  description = 'Please enter the password to access this page.',
  placeholder = 'Enter password',
  submitButtonText = 'Submit',
  errorMessage = 'Incorrect password. Please try again.',
}) => {
  // Initialize authentication state from sessionStorage
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem(storageKey) === 'true';
  });

  const [inputPassword, setInputPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Handle password submission
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');

    if (inputPassword === password) {
      setIsAuthenticated(true);
      sessionStorage.setItem(storageKey, 'true');
      setInputPassword('');
    } else {
      setPasswordError(errorMessage);
      setInputPassword('');
    }
  };

  // Show password prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div
        className="container-fluid px-3 py-2 d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
          <div className="card-header">
            <h5 className="mb-0">{title}</h5>
          </div>
          <div className="card-body">
            <p className="text-muted mb-3">{description}</p>
            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                  id="password"
                  value={inputPassword}
                  onChange={(e) => {
                    setInputPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder={placeholder}
                  autoFocus
                />
                {passwordError && (
                  <div className="invalid-feedback">{passwordError}</div>
                )}
              </div>
              <button type="submit" className="btn btn-primary w-100">
                {submitButtonText}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Render children when authenticated
  return <>{children}</>;
};

export default PasswordProtection;
